/* eslint-disable camelcase */
/* Imports */
import router from '../router/router.js';

export default {
  state: {
    queryParams: {},
  },
  getters: {
    getQueryParams: (state) => state.queryParams,
    getHasAppliedFilters: (state) => {
      const queryParams = state.queryParams || {};

      // List of query params that are NOT considered filters
      // Easier to list non-filter params since filters can be dynamic and there can be many
      const nonFilterParams = new Set([
        'sorter',
        'currentPage',
        'utm_medium',
        'utm_source',
        'utm_campaign',
        'utm_content',
        'utm_term',
        'cdncache',
        'skipRouter',
        'fbclid',
        'source',
        'loadData',
        'initialLoad',
        'compare',
        'compareProduct',
      ]);

      return Object.entries(queryParams).some(([key, value]) => !nonFilterParams.has(key) && value !== null && value !== '');
    },
  },
  mutations: {
    setQueryParams: (state, value) => {
      // eslint-disable-next-line no-param-reassign
      state.queryParams = value;
    },
  },
  actions: {
    setQueryParams: async ({ commit, getters, dispatch }, payload = {}) => {
      /**
       * If compare / compareProduct query params are set in the current url,
       * and compare / compareProduct are not being explicitly passed in the payload,
       * then persist the current compare / compareProduct query params.
       */
      const {
        compare: currentCompare,
        compareProduct: currentCompareProduct,
      } = getters.getQueryParams;

      if (currentCompare && !payload?.compare) {
        // eslint-disable-next-line no-param-reassign
        payload.compare = currentCompare;
      }
      if (currentCompareProduct && !payload?.compareProduct) {
        // eslint-disable-next-line no-param-reassign
        payload.compareProduct = currentCompareProduct;
      }

      const queryParamsObj = payload;

      // loadData: this value is true if it's necessary to make an API call to fetch the products, prices and filters
      const { loadData, initialLoad } = queryParamsObj;
      delete queryParamsObj?.loadData;

      if (initialLoad) {
        delete queryParamsObj.initialLoad;
      } else {
        // If the user is on the page and they change the sort order
        // we want to update the query params, but remove the skipRouter
        // from the url

        const queryPayload = payload;
        delete queryPayload.skipRouter;

        router.push({ path: '/', query: queryPayload });
      }
      commit('setQueryParams', payload); // allowed sorter to be set on the queryParams above

      // but removing the sorter before deciding whether to display grouped or ungrouped products
      // since the sorter should not affect this strippedPayload will contain
      // everything in payload except the sorter and currentPage key:value pairs
      // Remove all the utm_* params that are passed for email campaigns because we still want to
      // display groups for those
      const {
        sorter,
        currentPage,
        utm_medium,
        utm_source,
        utm_campaign,
        utm_content,
        utm_term,
        cdncache,
        skipRouter,
        fbclid,
        source,
        compare,
        compareProduct,
        ...strippedPayload
      } = payload;

      commit('setCurrentPage', currentPage || 1);
      // Ignore empty query params
      Object.entries(strippedPayload).forEach(([key, value]) => {
        if (value === null) {
          delete strippedPayload[key];
        }
      });

      const shouldLoadDisplayGroup = Object.keys(strippedPayload)?.length && !getters.getHasAppliedFilters;

      if (shouldLoadDisplayGroup) {
        dispatch('loadDisplayGroup', false);
      } else {
        dispatch('loadDisplayGroup', true);
      }

      const shouldLoadData = loadData || initialLoad;
      if (shouldLoadData) {
        const promises = [dispatch('loadProducts')];
        if (getters.getHasFilters) {
          const filters = dispatch('loadFilters');
          promises.push(filters);
        }

        const results = await Promise.allSettled(promises);
        const failures = results.filter((result) => result.status === 'rejected');
        if (failures.length) {
          // eslint-disable-next-line no-console
          console.error('An error occurred:', failures.map((f) => f.reason));
        }
      }
    },
  },
};
